/**
 * NotFoundPage — the catch-all `*` route target. Renders the same
 * branded design as RouteErrorPage but with a hard-coded 404 status
 * so the visual identity is preserved when a genuine unknown URL is
 * requested (typos, stale bookmarks, retired routes).
 */
import { useEffect, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  ArrowLeft, HouseSimple, Lifebuoy, WarningCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/config/routes";

export default function NotFoundPage() {
  const nav = useNavigate();
  const { pathname } = useLocation();

  const ref = useMemo(
    () => `nf-${Date.now().toString(36).slice(-6)}-${Math.random().toString(36).slice(2, 6)}`,
    [],
  );

  useEffect(() => {
    try {
      const payload = {
        ref, status: 404, pathname,
        ts: new Date().toISOString(),
        ua: navigator.userAgent.slice(0, 200),
      };
      const url = `${(process.env.REACT_APP_BACKEND_URL || "")}/api/logs/frontend-error`;
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob(
          [JSON.stringify(payload)], { type: "application/json" }));
      }
    } catch { /* silent */ }
  }, [ref, pathname]);

  return (
    <div
      data-testid="not-found-page"
      data-error-status={404}
      className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-4 py-10"
    >
      <div className="w-full max-w-xl bg-white border border-slate-200 rounded-lg shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_36px_-24px_rgba(15,23,42,0.16)] overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center gap-3">
          <img src="/sres-mark.png?v=6" alt="SRES" className="h-8 w-8 object-contain" />
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
              Shanvi Resources
            </span>
            <span className="text-[11px] uppercase tracking-widest text-slate-400">
              Ref&nbsp;<span data-testid="not-found-ref" className="text-slate-600 font-mono">{ref}</span>
            </span>
          </div>
        </div>

        <div className="px-6 py-8 flex flex-col items-start gap-5">
          <div className="h-12 w-12 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
            <WarningCircle size={22} weight="duotone" className="text-brand-blue" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
              404
            </div>
            <h1
              data-testid="not-found-title"
              className="mt-2 font-heading font-black text-3xl tracking-tight text-slate-900"
            >
              Page not found
            </h1>
            <p
              data-testid="not-found-body"
              className="mt-3 text-[14px] text-slate-600 leading-relaxed max-w-md"
            >
              The page you requested does not exist, may have been moved,
              or you may not have permission to access it.
            </p>
            <p className="mt-2 text-[12px] text-slate-500 font-mono truncate">
              {pathname}
            </p>
          </div>

          <div className="mt-1 flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => nav(-1)}
              data-testid="not-found-back"
              className="gap-2"
            >
              <ArrowLeft size={14} /> Go back
            </Button>
            <Button
              onClick={() => nav(ROUTES.dashboard)}
              data-testid="not-found-dashboard"
              className="gap-2 bg-brand-blue hover:bg-brand-blue/90 text-white"
            >
              <HouseSimple size={14} /> Open Dashboard
            </Button>
          </div>

          <div className="pt-4 mt-2 border-t border-slate-100 w-full flex items-center gap-2 text-[12px] text-slate-500">
            <Lifebuoy size={14} className="text-slate-400" />
            <span>Need help?</span>
            <Link
              to={ROUTES.support}
              data-testid="not-found-contact"
              className="text-brand-blue hover:underline"
            >
              Contact an administrator
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
